import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import { Flow } from '@thive/fddm-types';

import { Accordion } from '@/modules/common/components/Accordion';
import { InfoText } from '@common/components/InfoText';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { FddmServiceFactory as FloorplanDesignServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

import { FlowCard } from '..';

function RelatedFlowsComponent() {
  const { t } = useTranslation('interface');
  const fddmSvc = useMemo(
    () => FloorplanDesignServiceFactory.getInstance().createFddmService(),
    [],
  );
  const selectedAreaIds = useAppStore((state) => state.selectedIds);
  // Subscribing to `design.flows` (flows + flowStops) re-runs the selector
  // below whenever flows are added/removed/edited from anywhere.
  const designFlows = useFloorplanDesignStore((state) => state.design.flows);
  const getRelatedFlows = useFloorplanDesignStore((state) => state.getRelatedFlows);

  const flows = useMemo(
    () => (selectedAreaIds.length === 0 ? [] : getRelatedFlows(selectedAreaIds)),
    [selectedAreaIds, designFlows, getRelatedFlows],
  );

  const onDelete = useCallback(
    (flow: Flow) => {
      fddmSvc.deleteFlows([flow]);
    },
    [fddmSvc],
  );

  return (
    <Accordion
      title={t('properties.flows.header', { count: flows.length })}
      unmountOnExit
      sx={{ px: 2 }}
    >
      Related Flows
      <Stack>
        {flows.length ? (
          flows.map((flow) => (
            <FlowCard
              tooltipTitle={t('properties.flows.delete.tooltip')}
              flow={flow}
              key={flow.id}
              onDelete={onDelete}
            />
          ))
        ) : (
          <InfoText
            header={t('properties.flows.related_flows.no_flows.title')}
            subHeader={t('properties.flows.related_flows.no_flows.subtitle')}
            sx={{ width: '90%', margin: '1rem auto' }}
          />
        )}
      </Stack>
    </Accordion>
  );
}

export const RelatedFlows = memo(RelatedFlowsComponent);
